import { forwardRef, memo } from "react";
import type { FilterValue } from "@/components/filter-popover";
import { TableCell, TableRow } from "@/components/ui/table";
import type { Activity } from "@/hooks/api/use-account-activities";
import { cn } from "@/lib/utils";
import { MIN_DAYS_FOR_GAP } from "@/pages/accounts/account-explorer/touchpoints-table/constants";
import { calculateGapHeight } from "@/pages/accounts/account-explorer/touchpoints-table/hooks/use-virtualized-rows";
import type {
  ActivityRowProps,
  CollapsedActivity,
  FilterMode,
  GapRow,
} from "@/pages/accounts/account-explorer/touchpoints-table/types";
import { matchesDynamicFilters } from "@/pages/accounts/account-explorer/touchpoints-table/utils";
import {
  ChannelCell,
  DateCell,
  DirectionCell,
  ExpandCell,
  GutterCell,
  PeopleCell,
  StatusCell,
  TeamCell,
} from "./table-cells";

/**
 * Gets the appropriate class name for a row based on its state and filter matches
 */
export function getRowClassName(
  item: Activity | CollapsedActivity,
  isExpanded: boolean,
  filterMode: FilterMode,
  filters: Record<string, FilterValue>,
  selectedActivity: Activity | CollapsedActivity | null,
  focusedActivityId?: string,
): string {
  const citedTouchpointIds = filters.citedTouchpointIds as string[] | undefined;

  let matches = false;
  if (citedTouchpointIds) {
    // Special case: if citedTouchpointIds filter is active, use it exclusively
    matches = citedTouchpointIds.includes(item.id);
  } else {
    // Default filter matching logic
    matches = matchesDynamicFilters(item, filters);
  }

  const isCurrentlyViewing = focusedActivityId
    ? item.id === focusedActivityId
    : selectedActivity && item.id === selectedActivity.id;

  return cn("cursor-pointer transition-colors", {
    "hover:bg-muted/50": !isCurrentlyViewing,
    "bg-muted border-l-2 border-l-primary": isExpanded,
    "opacity-40": filterMode === "highlight" && !matches,
    "collapsed-row": "activities" in item,
    "bg-primary/10 dark:bg-primary/20 border border-primary/50":
      isCurrentlyViewing,
  });
}

type ExtraRowProps = {
  selectedPeople?: string[];
};

const ActivityRowInner = forwardRef<
  HTMLTableRowElement,
  ActivityRowProps & ExtraRowProps
>(
  (
    {
      item,
      isExpanded,
      index,
      onToggleExpand,
      onActivityClick,
      className,
      accountId,
      isFirstInThread,
      isLastInThread,
      isInThread,
      showThreadLine,
      selectedPeople,
      getPersonNameOverride,
      getPersonEmailOverride,
    },
    ref,
  ) => {
    const handleClick = () => {
      onActivityClick(item as Activity);
      if ("activities" in item && onToggleExpand) {
        onToggleExpand(item.id);
      }
    };

    // Only apply bold font if this is a parent row (has activities) and is expanded
    const shouldBeBold = "activities" in item && isExpanded;

    return (
      <TableRow
        data-index={index}
        data-row-id={item.id}
        data-timestamp={item.date}
        data-end-timestamp={"activities" in item ? item.endDate : item.date}
        className={className}
        onClick={handleClick}
        ref={ref}
      >
        <GutterCell
          isFirstInThread={isFirstInThread}
          isLastInThread={isLastInThread}
          isInThread={isInThread}
          showThreadLine={showThreadLine}
        />
        <DirectionCell item={item} isInThread={isInThread} />
        <ExpandCell
          item={item}
          isExpanded={isExpanded}
          className={isExpanded ? "border-l-2 border-l-primary" : ""}
          onClick={onToggleExpand}
        />
        <DateCell
          item={item}
          className={shouldBeBold ? "font-medium" : ""}
          data-column-id="date"
        />
        <TableCell
          className={cn(
            "w-[300px] py-3 px-2 border-b border-r border-border/50",
            shouldBeBold && "font-medium",
          )}
          data-column-id="activity"
        >
          <div className="cell-content line-clamp-2" title={item.activity}>
            {item.activity}
          </div>
        </TableCell>
        <TableCell
          className={cn(
            "w-[120px] py-3 px-2 border-b border-r border-border/50 truncate whitespace-nowrap",
            shouldBeBold && "font-medium",
          )}
          data-column-id="people"
        >
          <div className="cell-content">
            <PeopleCell
              people={item.people}
              accountId={accountId}
              selectedPeople={selectedPeople}
              getPersonNameOverride={getPersonNameOverride}
              getPersonEmailOverride={getPersonEmailOverride}
            />
          </div>
        </TableCell>
        <TableCell
          className={cn(
            "w-[120px] py-3 px-2 border-b border-r border-border/50 truncate whitespace-nowrap",
            shouldBeBold && "font-medium",
          )}
          data-column-id="channel"
        >
          <div className="cell-content" title={item.channel}>
            <ChannelCell channel={item.channel} />
          </div>
        </TableCell>
        <TableCell
          className={cn(
            "w-[100px] py-3 px-2 border-b border-r border-border/50 truncate whitespace-nowrap",
            shouldBeBold && "font-medium",
          )}
          data-column-id="status"
        >
          <div className="cell-content" title={item.status}>
            <StatusCell status={item.status} />
          </div>
        </TableCell>
        <TableCell
          className={cn(
            "py-3 px-2 border-b border-r border-border/50 truncate whitespace-nowrap",
            shouldBeBold && "font-medium",
          )}
          data-column-id="team"
        >
          <div
            className="cell-content"
            title={item.involved_team_ids?.join(", ") || ""}
          >
            <TeamCell teamIds={item.involved_team_ids} />
          </div>
        </TableCell>
      </TableRow>
    );
  },
);

export const ActivityRow = memo(ActivityRowInner);

/**
 * Gap row component for rendering time gaps between activities
 */
interface GapRowProps {
  item: GapRow;
  index: number;
}

const GapRowInner = forwardRef<HTMLTableRowElement, GapRowProps>(
  ({ item, index }, ref) => {
    const isSmallGap = item.days <= MIN_DAYS_FOR_GAP;
    const gapHeight = calculateGapHeight(item.days);

    // For rendered gaps with small heights, allow badge overflow
    const allowBadgeOverflow = !isSmallGap && gapHeight <= 30;

    const gapRowStyle = isSmallGap
      ? undefined
      : allowBadgeOverflow
        ? { height: 8 } // Force small height for overflow
        : { height: gapHeight };

    return (
      <TableRow
        data-index={index}
        key={item.id}
        ref={ref}
        data-row-id={item.id}
        data-gap-row={true}
        data-gap-days={item.days}
        style={gapRowStyle}
        className={cn("pointer-events-none")}
      >
        <TableCell
          className={cn("p-0 border-0", allowBadgeOverflow ? "h-2" : "")}
        />
        <TableCell
          colSpan={8}
          className={cn(
            "p-0 border-0 relative overflow-visible",
            allowBadgeOverflow ? "h-2" : "",
          )}
          style={
            !isSmallGap
              ? {
                  backgroundImage:
                    "repeating-linear-gradient(45deg, hsl(var(--gap-pattern)), hsl(var(--gap-pattern)) 2px, transparent 2px, transparent 6px)",
                }
              : {}
          }
        >
          <div
            className={cn(
              "relative",
              allowBadgeOverflow ? "h-0" : "h-full flex items-center",
            )}
          >
            <span className="absolute left-[80px] top-1/2 -translate-y-1/2 bg-background border border-border/20 px-1.5 py-0.5 rounded shadow-sm text-xs text-muted-foreground whitespace-nowrap z-10">
              {item.days} day gap
            </span>
          </div>
        </TableCell>
      </TableRow>
    );
  },
);

export const GapRowComponent = memo(GapRowInner);
